// Set-password page · /#/set-password?token=…
// Standalone (no login required) — a new hire opens the invite link, the
// page validates the token, then they pick a password and land logged in.

import { useEffect, useState } from 'react';
import { validateInvite, setPassword as apiSetPassword } from '../lib/api.ts';
import { VavaLogo } from '../components/VavaLogo.jsx';

// Friendly copy for every way a token can fail — never a raw 5xx / code.
const REASON_TEXT = {
  not_found: '邀请链接无效，请向管理员确认。',
  used: '该链接已被使用。如需重置密码，请联系管理员。',
  expired: '链接已过期，请向管理员重新申请。',
  not_invited: '该账号当前无法通过此链接设置密码，请联系管理员。'
};

function tokenFromHash() {
  const m = window.location.hash.match(/[?&]token=([^&]+)/);
  return m ? decodeURIComponent(m[1]) : '';
}

export const SetPassword = ({ onLogin }) => {
  const [phase, setPhase] = useState('checking');   // checking | form | error
  const [message, setMessage] = useState('');
  const [name, setName] = useState('');
  const [token] = useState(tokenFromHash);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) {
      setPhase('error');
      setMessage('链接无效 —— 缺少邀请令牌。');
      return;
    }
    let cancelled = false;
    validateInvite(token)
      .then((res) => {
        if (cancelled) return;
        if (res.valid) {
          setName(res.staff?.name || '');
          setPhase('form');
        } else {
          setMessage(REASON_TEXT[res.reason] || '邀请链接无效，请向管理员确认。');
          setPhase('error');
        }
      })
      .catch(() => {
        if (cancelled) return;
        setMessage('无法连接服务器，请稍后重试或联系管理员。');
        setPhase('error');
      });
    return () => { cancelled = true; };
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    setErr('');
    if (pw.length < 8) return setErr('密码至少 8 位');
    if (pw !== pw2) return setErr('两次输入的密码不一致');
    setSaving(true);
    try {
      const { staff } = await apiSetPassword(token, pw);
      onLogin(staff);
    } catch (e) {
      // A token can lapse between page load and submit — show the reason.
      setErr('⚠ ' + (e?.message || '设置失败，请重试'));
      setSaving(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
            <VavaLogo size={42} />
            <div>
              <div className="word">vava</div>
              <div className="sub">designer cakes</div>
            </div>
          </div>
          <div className="system">设置登录密码</div>
        </div>

        {phase === 'checking' && (
          <div style={{ textAlign: 'center', color: 'var(--vava-text-muted)', padding: '24px 0' }}>
            正在验证邀请链接…
          </div>
        )}

        {phase === 'error' && (
          <div style={{ padding: '8px 0 4px' }}>
            <div className="login-error">{message}</div>
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <a href="#/" style={{ color: 'var(--vava-tan-hover)', fontSize: 13 }}>返回登录</a>
            </div>
          </div>
        )}

        {phase === 'form' && (
          <form className="login-form" onSubmit={submit}>
            <div style={{ marginBottom: 12, color: 'var(--vava-text-muted)', fontSize: 13 }}>
              {name ? `${name}，你好！` : '你好！'}请为你的账号设置登录密码。
            </div>
            <div style={{ marginBottom: 16 }}>
              <label className="vava-label">新密码（至少 8 位）</label>
              <input type="password" className="vava-input" placeholder="新密码"
                     value={pw} onChange={(e) => setPw(e.target.value)}
                     autoFocus disabled={saving} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label className="vava-label">确认密码</label>
              <input type="password" className="vava-input" placeholder="再次输入密码"
                     value={pw2} onChange={(e) => setPw2(e.target.value)}
                     disabled={saving} />
            </div>
            <button type="submit" className="btn btn-primary"
                    style={{ width: '100%', justifyContent: 'center' }}
                    disabled={saving}>
              {saving ? '设置中…' : '设置密码并登录'}
            </button>
            {err && <div className="login-error">{err}</div>}
          </form>
        )}

        <div className="login-footer">VaVa Designer Cakes · Internal Use Only</div>
      </div>
    </div>
  );
};
